import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { resolveImg, formatPrice } from "@/lib/api";

export const ProductCard = ({ product, index = 0, compact = false }) => {
  const imgs = product.images?.length ? product.images : [""];
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  // Highest slide index revealed so far; hidden alternates are not fetched
  // until the hover slideshow actually reaches them.
  const [seen, setSeen] = useState(0);

  const variantPrices = (product.variants || []).map((v) => v.price).filter((p) => p != null);
  const priceLabel = variantPrices.length
    ? `From ${formatPrice(Math.min(...variantPrices))}`
    : formatPrice(product.price);

  useEffect(() => {
    if (!hover || imgs.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), 900);
    return () => clearInterval(t);
  }, [hover, imgs.length]);

  useEffect(() => { if (!hover) setIdx(0); }, [hover]);
  useEffect(() => { setSeen((s) => (idx > s ? idx : s)); }, [idx]);

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group block animate-fade-up"
      style={{ animationDelay: `${(index % 4) * 60}ms` }}
    >
      <div
        className={`relative overflow-hidden bg-white ${compact ? "aspect-[3/4]" : "aspect-[4/5]"}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          className="flex h-full w-full transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {imgs.map((img, i) =>
            i <= seen ? (
              <img
                key={i}
                src={resolveImg(img)}
                alt={product.name}
                loading="lazy"
                className="h-full w-full shrink-0 object-cover"
              />
            ) : (
              <div key={i} className="h-full w-full shrink-0 bg-white" aria-hidden="true" />
            )
          )}
        </div>
        {imgs.length > 1 && (
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
            {imgs.map((_, i) => (
              <span key={i} className={`h-1 w-1 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/50"}`} />
            ))}
          </div>
        )}
      </div>
      <div className={compact ? "mt-3 text-center" : "mt-5 text-center"}>
        <h3 className={`font-serif text-stone-900 ${compact ? "text-base md:text-lg" : "text-xl md:text-2xl"}`}>{product.name}</h3>
        {product.cttw && <p className={`text-stone-500 ${compact ? "mt-1 text-xs" : "mt-1.5 text-sm"}`} data-testid={`cttw-${product.id}`}>{product.cttw}</p>}
        <p className={`font-bold text-stone-900 ${compact ? "mt-1 text-sm" : "mt-2 text-lg"}`} data-testid={`price-${product.id}`}>{priceLabel}</p>
      </div>
    </Link>
  );
};
